import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  // Check for existing session on component mount
  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Check if user had "Keep me logged in" enabled
        const rememberMeTimestamp = localStorage.getItem('ibv-remember-me');
        const explicitlyDeclined = localStorage.getItem('ibv-no-remember');
        
        // If user explicitly declined to be remembered, sign out
        if (explicitlyDeclined === 'true') {
          await supabase.auth.signOut();
          localStorage.removeItem('ibv-no-remember');
          setCheckingSession(false);
          return;
        }
        
        // If there's a timestamp, check if it's expired
        if (rememberMeTimestamp) {
          const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
          const loginTime = parseInt(rememberMeTimestamp);
          const isExpired = Date.now() - loginTime > sevenDaysInMs;
          
          if (isExpired) {
            // Session expired, sign out
            await supabase.auth.signOut();
            localStorage.removeItem('ibv-remember-me');
            setCheckingSession(false);
            return;
          }
        }
        
        // Valid session - proceed with login
        // (either has valid timestamp or no explicit decline)
        setIsLoggingIn(true);
        const userId = session.user.id;
        
        // Check if admin
        const { data: adminData } = await supabase
          .from('admins')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (adminData?.is_active) {
          if (session.user.email?.toLowerCase() === 'londonvip@wealthclub.com') {
            navigate('/london-vip-admin');
          } else {
            navigate('/admin');
          }
          return;
        }

        // Check regular user
        const { data: userData } = await supabase
          .from('users')
          .select('status')
          .eq('id', userId)
          .single();

        if (userData?.status === 'active') {
          navigate('/dashboard');
          return;
        }
      }
    } catch (error) {
      console.error('Session check error:', error);
    } finally {
      setIsLoggingIn(false);
      setCheckingSession(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoggingIn(true);
    
    try {
      // Store "Keep me logged in" preference BEFORE login
      if (rememberMe) {
        localStorage.setItem('ibv-remember-me', Date.now().toString());
        localStorage.removeItem('ibv-no-remember');
      } else {
        localStorage.setItem('ibv-no-remember', 'true');
        localStorage.removeItem('ibv-remember-me');
      }

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email.trim(),
        password: formData.password
      });

      if (authError) {
        console.error('Auth error:', authError);
        setError(authError.message || 'Invalid email or password');
        setIsLoggingIn(false);
        return;
      }

      if (!authData?.user) {
        setError('Authentication failed. Please try again.');
        setIsLoggingIn(false);
        return;
      }

      // Check if admin
      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('*')
        .eq('user_id', authData.user.id)
        .single();

      if (adminData && !adminError && adminData.is_active) {
        // Refresh the "Keep me logged in" timestamp
        if (rememberMe) {
          localStorage.setItem('ibv-remember-me', Date.now().toString());
        }
        
        // Check if London VIP admin email
        if (formData.email.trim().toLowerCase() === 'londonvip@wealthclub.com') {
          navigate('/london-vip-admin');
        } else {
          navigate('/admin');
        }
        setIsLoggingIn(false);
        return;
      }

      // Check regular user
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('status')
        .eq('id', authData.user.id)
        .single();

      if (userError || !userData) {
        setError('User not found. Please contact support.');
        await supabase.auth.signOut();
        setIsLoggingIn(false);
        return;
      }

      if (userData.status === 'pending') {
        await supabase.auth.signOut();
        setError('Your application is pending approval.');
        setIsLoggingIn(false);
        return;
      }

      if (userData.status === 'declined') {
        await supabase.auth.signOut();
        setError('Your application was declined.');
        setIsLoggingIn(false);
        return;
      }

      // Success - navigate to dashboard
      // Refresh the "Keep me logged in" timestamp
      if (rememberMe) {
        localStorage.setItem('ibv-remember-me', Date.now().toString());
      }
      
      navigate('/dashboard');
      setIsLoggingIn(false);
      
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please check your credentials and try again.');
      setIsLoggingIn(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setSendingReset(true);
    setResetError('');
    setResetSuccess(false);

    console.log('🔵 Starting password reset for:', resetEmail.trim());
    console.log('🔵 Redirect URL:', `${window.location.origin}/reset-password`);

    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: `${window.location.origin}/reset-password`
      });

      console.log('📧 Reset email response:', { data, error });

      if (error) throw error;

      console.log('✅ Password reset email sent successfully');
      
      // Check if running on localhost
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        setResetSuccess(true);
        setResetError('Development Mode: Check your Supabase Dashboard → Authentication → Logs to find the reset link (Inbucket).');
      } else {
        setResetSuccess(true);
      }
      
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess(false);
        setResetEmail('');
        setResetError('');
      }, 8000);
    } catch (err) {
      console.error('❌ Password reset error:', err);
      
      // Provide helpful error messages
      let errorMessage = err.message || 'Failed to send reset email.';
      
      if (err.message?.includes('Email') || err.message?.includes('email')) {
        errorMessage = 'Email service not configured. For localhost testing, check Supabase Dashboard → Authentication → Logs for the reset link.';
      } else if (err.message?.includes('User not found')) {
        errorMessage = 'No account found with this email address.';
      } else if (err.message?.includes('rate limit')) {
        errorMessage = 'Too many requests. Please wait a few minutes and try again.';
      }
      
      setResetError(errorMessage);
    } finally {
      setSendingReset(false);
    }
  };

  // Show loading spinner while checking for existing session
  if (checkingSession) {
    return (
      <div className="page-container">
        <div className="page-wrapper" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh'}}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-wrapper">
        <div className="logo-container" style={{textAlign: 'center', marginBottom: '2rem'}}>
          <img src="/images/IBVlogo.png" alt="IBV Wealth Club" style={{maxWidth: '150px', height: 'auto'}} />
        </div>

        <div className="card">
          {!showForgotPassword ? (
            <>
              <h2 className="section-title text-center">Sign in to your account</h2>

              {error && <div className="alert alert-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Membership ID</label>
                  <input
                    type="text"
                    name="email"
                    className="form-input"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    name="password"
                    className="form-input"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <a 
                    href="#" 
                    className="edit-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowForgotPassword(true);
                      setResetEmail(formData.email);
                    }}
                  >
                    Forgot Password?
                  </a>
                </div>

                <div className="form-group">
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#666'
                  }}>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      style={{
                        marginRight: '8px',
                        cursor: 'pointer',
                        width: '16px',
                        height: '16px',
                        accentColor: '#CDB688'
                      }}
                    />
                    Keep me logged in for 7 days
                  </label>
                </div>

                <button type="submit" className="btn btn-primary" disabled={isLoggingIn}>
                  {isLoggingIn ? 'Logging in...' : 'Login'}
                </button>

                <button 
                  type="button" 
                  className="btn btn-secondary mt-md"
                  onClick={() => navigate('/register')}
                >
                  Register
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="section-title text-center">Reset Password</h2>
              
              {resetSuccess && (
                <div className="alert alert-success" style={{marginBottom: '1rem'}}>
                  <strong>✅ Request Sent!</strong>
                  <br />
                  {window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? (
                    <>
                      <small>
                        <strong>Development Mode Detected</strong><br />
                        Since you're testing on localhost, check your Supabase Dashboard:<br />
                        1. Go to <strong>Authentication → Logs</strong><br />
                        2. Find the password reset email<br />
                        3. Copy the reset link and paste it in your browser<br />
                        <br />
                        Alternatively, configure SMTP settings in Supabase to receive actual emails.
                      </small>
                    </>
                  ) : (
                    'Password reset email sent! Please check your inbox.'
                  )}
                </div>
              )}
              
              {resetError && (
                <div className="alert alert-error">
                  {resetError}
                  {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                    <div style={{marginTop: '10px', fontSize: '12px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '10px'}}>
                      <strong>💡 Localhost Testing Tip:</strong><br />
                      Email might not work on localhost. Check browser console and Supabase Dashboard → Authentication → Logs for the reset link.
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Enter your email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                  <p style={{
                    fontSize: '13px',
                    color: '#666',
                    marginTop: '8px'
                  }}>
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={sendingReset}
                >
                  {sendingReset ? 'Sending...' : 'Send Reset Link'}
                </button>

                <button 
                  type="button" 
                  className="btn btn-secondary mt-md"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                    setResetError('');
                    setResetSuccess(false);
                  }}
                >
                  Back to Login
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
